<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2019-2023 (original work) Open Assessment Technologies SA ;
    import { tabFocus } from '@oat-sa-private/ui-core';
    import { createEventDispatcher } from 'svelte';
    import validationFactory from './inputValidation.js';
    import { allFeedbacks } from '../feedback/index.js';
    import Icon from '../icon/Icon.svelte';

    /**
     * @typedef {Object} FeedbackProps
     * @property {boolean} showValidFeedback
     * @property {boolean} showInvalidFeedback
     */

    /**
     * @typedef {Object} CustomValidity
     * @property {string} msg
     * @property {boolean} valid
     */

    /**
     * Component is used to display inline text/password input
     * @property {string} [value=''] Corresponds to native HTML `value` attribute
     * @property {string} name Corresponds to native HTML `name` attribute
     * @property {string} [type='text'] Corresponds to native HTML `type` attribute
     * @property {number} [size=16] width defined as a count of visible letters
     * @property {string} [placeholder=''] Corresponds to native HTML `placeholder` attribute
     * @property {boolean} [disabled=false] Corresponds to native HTML `disabled` attribute
     * @property {boolean} [readonly=false] Corresponds to native HTML `readonly` attribute
     * @property {string} ariaDescribedBy Corresponds to native HTML `aria-describedby` attribute
     * @property {boolean} [required=false] Corresponds to native HTML `required` attribute
     * @property {string} [pattern=null] Corresponds to native HTML `pattern` attribute
     * @property {number} [maxlength=null] Corresponds to native HTML `maxlength` attribute
     * @property {number} [minlength=null] Corresponds to native HTML `minlength` attribute
     * @property {string} [patternMessage=''] (Requires `pattern`) Custom feedback message for the pattern validation
     * @property {string} [height='medium'] The vertical height of the input: `small`, `medium` or `large`
     * @property {boolean} [fullwidth=false] Should the `Input` display as block-level and fill its parent?
     * @property {string} [inputmode='text'] Allow to display correct keyboard in touch-based devices, can be `text`, `numeric` or `decimal`
     * @property {boolean} [hasIcon=false] Specifies if input should have padding right (for example to be able to display icon) (deprecated, use suffix slot instead)
     * @property {string} [feedback=''] Selects a feedback subcomponent from an internal list. Possible values: `inline`
     * @property {FeedbackProps} [feedbackProps={}] Configuration properties for the integrated InlineFeedback component
     * @property {string} feedbackLang Language code for feedback `lang` property
     * @property {Object|CustomValidity} [customValidity={valid: true}] - Validity object for invalidating an otherwise valid Input from the outside.
     * @property {boolean} [visited=false] Set the field as if it was already visited. Usually used to restore the input with a value. The validation will trigger accordingly
     * @fires change {CustomEvent} value change event, details contains new value
     * @fires validation {CustomEvent} fired when validation has performed with validity status in details
     */

    // size constants (size is a number of characters)
    const defaultSize = 16;
    const maxSize = 67;

    // native HTML attribute props
    export let value = '';
    export let name;
    export let type = 'text';
    export let size = defaultSize;
    export let placeholder = '';
    export let disabled = false;
    export let readonly = false;
    export let ariaDescribedBy;

    // validation props
    export let required = false;
    export let pattern = null;
    export let maxlength = null;
    export let minlength = null;

    // custom props
    export let patternMessage = ''; // qti-only property
    export let height = 'medium';
    export let fullwidth = false;
    export let inputmode = 'text';
    export let hasIcon = false;

    // props for dynamic feedback
    export let feedback = '';
    export let feedbackProps = {};
    export let feedbackLang;
    export let customValidity = { valid: true };
    export let visited = false; // true after first focus

    const heights = ['x-small', 'small', 'small-plus', 'medium', 'large'];
    if (!heights.includes(height)) {
        height = 'medium';
    }
    const types = ['text', 'password'];
    if (!types.includes(type)) {
        type = 'text';
    }
    if (maxlength && typeof maxlength !== 'number') {
        maxlength = parseInt(maxlength, 10);
        if (isNaN(maxlength)) {
            maxlength = void 0;
        }
    }
    if (minlength && typeof minlength !== 'number') {
        minlength = parseInt(minlength, 10);
        if (isNaN(minlength)) {
            minlength = void 0;
        }
    }

    const dispatch = createEventDispatcher();
    // Prepare validation
    const validate = validationFactory({ required, maxlength, minlength, pattern, patternMessage });

    let inputElement; // DOM element reference
    let prefixWrapper;
    let suffixWrapper;

    let firstTry = !visited; // false after first blur
    let focusFromKey = false; // class toggler instead of :focus-visible to prevent conflict with native
    let focus = false;

    let validity = getValidity();

    // re-validate when new customValidity received
    $: if (inputElement && customValidity) {
        validity = getValidity();
    }
    $: showPrefix = $$slots.prefix;
    $: prefixWidth = !showPrefix ? '0px' : `${prefixWrapper?.clientWidth}px`;
    $: showValidityIcon = !firstTry && !validity.overallValidity && feedback !== 'block';
    $: showSuffix = showValidityIcon || $$slots.suffix || hasIcon;
    $: suffixWidth = !showSuffix ? '0px' : `${suffixWrapper?.clientWidth}px`;
    $: fixedSizeLetters = Math.min(parseInt(size, 10) || defaultSize, maxSize);

    /**
     * Callback for when input's value changes
     * Also triggers validation, if applicable
     * @param {InputEvent} e
     * @fires change
     */
    function handleInput(e) {
        value = e.target.value;
        if (maxlength && value && value.length > maxlength) {
            //Android Chrome allows to type more characters, but cuts value on blur
            value = value.substring(0, maxlength);
            //update element value immediately so it recalculates validity.patternMismatch & validity.valueMissing
            e.target.value = value;
        }
        dispatch('change', { value, target: e.target });
        handleValidation();
    }

    function handleHitboxClick() {
        if (inputElement) {
            inputElement.focus();
        }
    }

    /**
     * Validate input value
     * @fires validation
     */
    function handleValidation() {
        validity = getValidity();
        dispatch('validation', { validity });
    }

    /**
     * Validate the input element and set local validity
     * @returns {Object{}} {messages=[], overallValidity=Boolean}
     */
    function getValidity() {
        return visited && inputElement
            ? validate(inputElement, customValidity, value)
            : { messages: [], overallValidity: true };
    }

    function triggerClipboardEvent(e) {
        const domEventType = e.type;
        const selectedContent = document.getSelection().toString();
        let content;
        let replacedContent;
        if (domEventType === 'paste') {
            content = e.clipboardData.getData('text/plain');
            replacedContent = selectedContent;
        } else {
            content = selectedContent;
        }
        dispatch('clipboard', {
            domEventType,
            target: e.target,
            content,
            replacedContent,
            position: e.target.selectionStart
        });
    }

    function triggerDragEvent(e) {
        dispatch('dragdrop', {
            domEventType: e.type,
            content: document.getSelection().toString(),
            position: e.target.selectionStart,
            target: e.target
        });
    }

    function triggerDropEvent(e) {
        const content = e.dataTransfer.getData('text/plain');
        dispatch('dragdrop', {
            domEventType: 'drop',
            content,
            target: e.target
        });
    }
</script>

<style>
    .positioning-wrapper {
        display: inline-block;
        position: relative;
        --padding-prefix: 2rem;
        --padding-suffix: 2rem;
        --padding-y: 1.5rem;
        --text-spacing-prefix: 0rem;
        --text-spacing-suffix: 0rem;
        --extra-size: 6.75rem; /* 2(one side input padding) + 4.5(error icon padding) + 2 * 0.125(border width)*/

        &.fullwidth {
            display: block;
            & input {
                min-width: 100%;
                max-width: unset;
            }
        }

        /* hitbox */
        &.height-x-small {
            line-height: 4rem;
            --padding-y: 0.625rem;

            &:before {
                content: '';
                position: absolute;
                cursor: text;
                top: -0.5rem;
                left: -0.5rem;
                right: -0.5rem;
                bottom: -0.5rem;
            }
            & input {
                position: relative;
            }
        }

        /* Sizing */
        &.height-x-small {
            /* padding + border + line-height = 4rem */
            --padding-y: 0.5rem;
            --padding-prefix: 0.75rem;
            --padding-suffix: 0.75rem;
            --extra-size: 5.5rem; /* 0.75(one side input padding) + 4.5(error icon padding) + 2 * 0.125(border width) */
        }
        &.height-small {
            --padding-y: 0.75rem;
            --padding-prefix: 1rem;
            --padding-suffix: 1rem;
            --extra-size: 5.75rem; /* 1(one side input padding) + 4.5(error icon padding) + 2 * 0.125(border width) */
        }
        &.height-small-plus {
            --padding-y: 1.25rem;
            --padding-prefix: 2rem;
            --padding-suffix: 2rem;
        }
        &.height-large {
            --padding-y: 2.25rem;
            --padding-prefix: 3rem;
            --padding-suffix: 3rem;
            --extra-size: 7.75rem; /* 3(one side input padding) + 4.5(error icon padding) + 2 * 0.125(border width) */
        }

        &.has-prefix {
            --text-spacing-prefix: 1rem;
            --padding-prefix: 0rem;
        }
        &.has-suffix {
            --text-spacing-suffix: 0.5rem;
            --padding-suffix: 0rem;
        }

        & .suffix-wrapper,
        & .prefix-wrapper {
            display: flex;
            /*
                input padding top plus padding bottom plus input line-height
                plus doubled border width in order to take whole input height
            */
            height: calc(var(--padding-y) * 2 + 2.75rem + var(--border-thin) * 2);
            align-items: center;
            position: absolute;
            top: 0;
            /* needed for xsmall size in order to propagate prefix and suffix above input */
            z-index: 1;
        }

        & .prefix-wrapper {
            inset-inline-start: 0;
        }

        & .suffix-wrapper {
            inset-inline-end: 0;
        }

        & :global(.feedback-block) {
            max-width: var(--input-width);
        }
    }

    :global(.inverted) input {
        color: var(--color-text-inverted);
        background: var(--color-bg-inverted);
        border-color: var(--color-border-inverted);

        &.key-focus-visible {
            @add-mixin simple-outline var(--color-border-focus-inverted);
        }
        &:disabled {
            background: var(--color-bg-disabled-inverted);
        }
        &:hover {
            border-color: var(--color-border-actionable-hover-inverted);
        }
        &:focus,
        &:active {
            @add-mixin double-border var(--color-border-actionable-hover-inverted);
        }
    }

    input {
        color: inherit;
        background: inherit;

        @add-mixin width-to-fit-letters var(--fixed-size-letters);
        --input-width: calc(var(--width-to-fit-letters) + var( --extra-size));

        width: var(--input-width);
        border: var(--border-thin) solid var(--color-border-default);
        box-sizing: border-box;
        padding-top: var(--padding-y);
        padding-bottom: var(--padding-y);
        padding-inline-start: calc(var(--padding-prefix) + var(--prefix-width) + var(--text-spacing-prefix));
        padding-inline-end: calc(var(--padding-suffix) + var(--suffix-width) + var(--text-spacing-suffix));
        max-width: 72rem;
        font-family: var(--font-ui);
        font-size: var(--fontsize-body);
        line-height: 2.75rem;
        letter-spacing: inherit;
        word-spacing: inherit;

        /* States */
        &:hover {
            border-color: var(--color-border-actionable-hover);
        }
        &:focus,
        &:active {
            outline: none;
            @add-mixin double-border var(--color-border-actionable-hover);
        }
        &.key-focus-visible {
            @add-mixin double-border var(--color-border-selected);
            @add-mixin simple-outline;
        }
        &:not(.valid),
        &:not(.valid).key-focus-visible {
            @add-mixin double-border var(--color-border-warning);
        }
        &:not(.valid):hover:not(:focus) {
            @add-mixin double-border var(--color-border-actionable-hover);
        }
        &[readonly],
        &[readonly].key-focus-visible,
        &:disabled,
        &:disabled:hover,
        &:disabled.key-focus-visible {
            color: var(--color-text-disabled);
            border-color: var(--color-border-disabled);
            box-shadow: none;
        }
        &:disabled {
            background: var(--color-bg-disabled-subtle);
        }
        &:not(.valid) {
            /* error icon */
            & ~ :global(svg) {
                position: absolute;
                right: 1.5rem;
                left: auto;
                top: calc(50% - 1rem);
                color: var(--color-alert);
            }
        }
    }

    .validity-icon-wrapper {
        display: flex;
        width: 3.5rem;
        padding-inline-end: 1rem;
        padding-inline-start: 0.5rem;
        align-items: center;
        justify-content: center;
        color: var(--color-border-warning);
    }
</style>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<span
    class="positioning-wrapper height-{height}"
    style={`
        --suffix-width: ${suffixWidth};
        --prefix-width: ${prefixWidth};
        --fixed-size-letters: ${fixedSizeLetters};
    `}
    class:fullwidth
    class:has-prefix={showPrefix}
    class:has-suffix={showSuffix}
    on:click={height === 'x-small' ? handleHitboxClick : void 0}>
    {#if showPrefix}
        <span class="prefix-wrapper" bind:this={prefixWrapper}>
            <slot name="prefix" />
        </span>
    {/if}
    <input
        {type}
        {name}
        {placeholder}
        {required}
        {pattern}
        {maxlength}
        {minlength}
        {readonly}
        {disabled}
        {inputmode}
        class="height-{height}"
        class:key-focus-visible={focusFromKey}
        class:valid={!visited || firstTry || validity.overallValidity}
        class:empty={!value.length}
        {value}
        use:tabFocus
        on:input={handleInput}
        on:focus|once={() => {
            visited = true;
            validity = getValidity();
        }}
        on:focus={handleValidation}
        on:focus={() => (focus = true)}
        on:focus
        on:tabfocus={() => (focusFromKey = true)}
        on:blur={() => {
            focusFromKey = false;
            focus = false;
        }}
        on:blur|once={() => {
            validity = getValidity();
            firstTry = false;
        }}
        on:blur
        aria-readonly={readonly}
        aria-invalid={!validity.overallValidity}
        aria-describedBy={ariaDescribedBy ? ariaDescribedBy : void 0}
        bind:this={inputElement}
        on:copy={triggerClipboardEvent}
        on:paste={triggerClipboardEvent}
        on:cut={triggerClipboardEvent}
        on:dragstart={triggerDragEvent}
        on:dragend={triggerDragEvent}
        on:drop={triggerDropEvent} />
    {#if showSuffix}
        <span class="suffix-wrapper" bind:this={suffixWrapper}>
            {#if showValidityIcon}
                <span class="validity-icon-wrapper">
                    <Icon name="warning-16" ariaHidden="true" />
                </span>
            {:else}
                <slot name="suffix" />
            {/if}
        </span>
    {/if}
    {#if feedback && ((feedback !== 'block' && focus) || (feedback === 'block' && !focus)) && !readonly && !disabled}
        <svelte:component
            this={allFeedbacks[feedback]}
            {validity}
            {...feedbackProps}
            lang={feedbackLang}
            emptyInput={!value.length}
            {firstTry} />
    {/if}
</span>
